import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  BarChart3,
  Users,
  Download,
  MousePointerClick,
  Link2,
  Megaphone,
  TrendingUp,
  Layers,
} from 'lucide-react';
import { Card, CardHeader, StatCard } from '@azyntor-labs/nexus-ui';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/api/client';
import { analyticsApi } from '@/api/endpoints';

// --- Interfaces matching actual backend response shapes ---

interface EnrollmentSource {
  exam_id: string;
  exam_title: string;
  enrollment_source: string;
  total_enrollments: number;
  unique_students: number;
  percentage_of_total: number | null;
}

interface Campaign {
  campaign_id: string;
  campaign_name?: string;
  utm_source?: string;
  utm_campaign?: string;
  total_enrollments: number;
  unique_students: number;
  budget?: number;
  cost_per_enrollment?: number;
}

interface MagicLink {
  link_id: string;
  link_name?: string;
  exam_id: string;
  exam_title: string;
  is_active: boolean;
  total_clicks: number;
  total_enrollments: number;
  conversion_rate?: number;
}

interface EnrollmentTrend {
  date: string;
  total_enrollments: number;
  source: string;
}

// --- Helper to safely extract array data from various API response shapes ---
function extractArray<T>(response: any): T[] {
  if (!response) return [];
  const inner = response?.data?.data;
  if (Array.isArray(inner)) return inner;
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

// --- Dark theme tooltip style (consistent with EnhancedAnalyticsDashboard) ---
const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: '#1f2937',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: '#fff',
  },
  itemStyle: { color: '#fff' },
  labelStyle: { color: '#fff', fontWeight: 600 },
};

// Chart color palette
const SOURCE_COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#10b981', '#6366f1'];

export const CampaignAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [sourceBreakdownPage, setSourceBreakdownPage] = useState(1);

  // Fetch enrollment sources
  const { data: sourcesData } = useQuery({
    queryKey: ['analytics-sources'],
    queryFn: () => apiClient.get('/analytics/campaigns/sources'),
  });

  // Fetch campaign performance
  const { data: campaignsData } = useQuery({
    queryKey: ['analytics-campaigns'],
    queryFn: () => apiClient.get('/analytics/campaigns/performance'),
  });

  // Fetch link performance
  const { data: linksData } = useQuery({
    queryKey: ['analytics-links'],
    queryFn: () => apiClient.get('/analytics/campaigns/links'),
  });

  // Fetch enrollment trends
  const { data: trendsData } = useQuery({
    queryKey: ['campaign-trends'],
    queryFn: () => analyticsApi.getCampaignTrends(),
  });

  // Extract data with safe fallbacks
  const sources: EnrollmentSource[] = extractArray<EnrollmentSource>(sourcesData);
  const campaigns: Campaign[] = extractArray<Campaign>(campaignsData);
  const links: MagicLink[] = extractArray<MagicLink>(linksData);
  const trends: EnrollmentTrend[] = extractArray<EnrollmentTrend>(trendsData);

  // --- Aggregate source data across all exams ---
  const aggregatedSources = useMemo(() => {
    const sourceMap = new Map<string, number>();
    for (const s of sources) {
      sourceMap.set(s.enrollment_source, (sourceMap.get(s.enrollment_source) || 0) + s.total_enrollments);
    }
    const total = Array.from(sourceMap.values()).reduce((a, b) => a + b, 0);
    return Array.from(sourceMap.entries())
      .map(([name, count]) => ({
        name: name.replace(/_/g, ' '),
        value: count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [sources]);

  // --- Prepare trends chart data ---
  const trendsChartData = useMemo(() => {
    if (trends.length === 0) return [];

    // Group by date, with a value per source
    const dateMap = new Map<string, Record<string, number>>();
    const allSources = new Set<string>();
    for (const t of trends) {
      const dateStr = t.date.split('T')[0];
      allSources.add(t.source);
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {});
      }
      const entry = dateMap.get(dateStr)!;
      entry[t.source] = (entry[t.source] || 0) + t.total_enrollments;
    }

    return Array.from(dateMap.entries())
      .map(([date, sourceCounts]) => ({
        date,
        ...sourceCounts,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [trends]);

  const trendSources = useMemo(() => {
    const s = new Set<string>();
    for (const t of trends) s.add(t.source);
    return Array.from(s);
  }, [trends]);

  const handleExport = () => {
    const baseUrl = apiClient.defaults.baseURL || import.meta.env.VITE_API_URL || '/api/v1';
    window.open(`${baseUrl}/analytics/campaigns/export`, '_blank');
  };

  // --- Compute stat card values from actual data ---
  const totalEnrollments = aggregatedSources.reduce((sum, s) => sum + s.value, 0);
  const uniqueSourceCount = aggregatedSources.length;
  const totalLinks = links.length;
  const activeLinks = links.filter((l) => l.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Campaign Analytics</h1>
          <p className="text-white/50 mt-1">
            ROI tracking, enrollment sources, and magic link performance
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-teal-500 hover:bg-teal-600 text-white transition-colors"
        >
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Analytics Navigation Tabs */}
      <div className="flex items-center space-x-1 bg-white/5 border border-white/[0.06] rounded-lg p-1">
        <button
          onClick={() => navigate('/analytics')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Standard
        </button>
        <button
          onClick={() => navigate('/analytics/enhanced')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Enhanced
        </button>
        <button className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-500 text-white">
          Campaigns
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Enrollments"
          value={totalEnrollments.toLocaleString()}
          icon={<Users />}
          color="teal"
          subtitle="All sources combined"
        />
        <StatCard
          title="Enrollment Sources"
          value={uniqueSourceCount}
          icon={<Layers />}
          color="purple"
          subtitle="Unique channels"
        />
        <StatCard
          title="Magic Links"
          value={totalLinks}
          icon={<Link2 />}
          color="blue"
          subtitle={`${activeLinks} active`}
        />
        <StatCard
          title="Active Campaigns"
          value={campaigns.length}
          icon={<Megaphone />}
          color="orange"
          subtitle={campaigns.length > 0 ? 'Running' : 'None yet'}
        />
      </div>

      {/* Enrollment Source Distribution - Pie + Bar side by side */}
      <Card>
        <CardHeader
          title="Enrollment by Source"
          subtitle="Aggregate enrollment distribution across all exams"
        />
        {aggregatedSources.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-2 pb-2">
            {/* Pie Chart */}
            <div>
              <p className="text-xs text-white/40 mb-3 font-medium uppercase tracking-wide">Distribution</p>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={aggregatedSources}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={2}
                    label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                    labelLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                  >
                    {aggregatedSources.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    formatter={(value: number, name: string) => [`${value.toLocaleString()} enrollments`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Bar Chart */}
            <div>
              <p className="text-xs text-white/40 mb-3 font-medium uppercase tracking-wide">By Count</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={aggregatedSources} layout="vertical" barSize={20}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    width={120}
                  />
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    formatter={(value: number) => [value.toLocaleString(), 'Enrollments']}
                  />
                  <Bar dataKey="value" name="Enrollments" radius={[0, 4, 4, 0]}>
                    {aggregatedSources.map((_entry, index) => (
                      <Cell key={`bar-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/40">No enrollment source data available</p>
          </div>
        )}
      </Card>

      {/* Per-exam source breakdown — compact table with pagination */}
      {sources.length > 0 && (() => {
        const perPage = 10;
        return (
          <Card>
            <CardHeader
              title="Source Breakdown by Exam"
              subtitle={`${sources.length} records — enrollment source per exam`}
            />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left py-2 px-4 text-xs font-semibold text-white/50 uppercase">Exam</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-white/50 uppercase">Source</th>
                    <th className="text-right py-2 px-4 text-xs font-semibold text-white/50 uppercase">Enrollments</th>
                    <th className="text-right py-2 px-4 text-xs font-semibold text-white/50 uppercase">Students</th>
                    <th className="py-2 px-4 text-xs font-semibold text-white/50 uppercase w-32"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {sources.slice((sourceBreakdownPage - 1) * perPage, sourceBreakdownPage * perPage).map((s, i) => {
                    const maxEnroll = Math.max(...sources.map(x => x.total_enrollments || 0), 1);
                    const pct = ((s.total_enrollments || 0) / maxEnroll) * 100;
                    return (
                      <tr key={`${s.exam_id}-${s.enrollment_source}-${i}`} className="hover:bg-white/[0.02]">
                        <td className="py-2 px-4 text-white/80 truncate max-w-[200px]" title={s.exam_title}>{s.exam_title}</td>
                        <td className="py-2 px-4"><span className="px-2 py-0.5 bg-teal-500/10 text-teal-400 rounded text-xs capitalize">{(s.enrollment_source || '').replace(/_/g, ' ')}</span></td>
                        <td className="py-2 px-4 text-right text-white font-medium">{s.total_enrollments}</td>
                        <td className="py-2 px-4 text-right text-white/50">{s.unique_students ?? '-'}</td>
                        <td className="py-2 px-4"><div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} /></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sources.length > perPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                <p className="text-xs text-white/40">Page {sourceBreakdownPage} of {Math.ceil(sources.length / perPage)}</p>
                <div className="flex gap-1">
                  <button onClick={() => setSourceBreakdownPage(p => Math.max(1, p - 1))} disabled={sourceBreakdownPage === 1}
                    className="px-2.5 py-1 text-xs rounded border border-white/[0.08] text-white/50 hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
                  <button onClick={() => setSourceBreakdownPage(p => Math.min(Math.ceil(sources.length / perPage), p + 1))} disabled={sourceBreakdownPage >= Math.ceil(sources.length / perPage)}
                    className="px-2.5 py-1 text-xs rounded border border-white/[0.08] text-white/50 hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
                </div>
              </div>
            )}
          </Card>
        );
      })()}

      {/* Enrollment Trends Over Time */}
      <Card>
        <CardHeader
          title="Enrollment Trends"
          subtitle="Enrollment volume over the last 30 days by source"
        />
        {trendsChartData.length > 0 ? (
          <div className="px-2 pb-2">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendsChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  tickFormatter={(val: string) => {
                    const d = new Date(val);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  allowDecimals={false}
                />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  labelFormatter={(label: string) => {
                    const d = new Date(label);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  }}
                />
                <Legend
                  wrapperStyle={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                />
                {trendSources.map((source, idx) => (
                  <Line
                    key={source}
                    type="monotone"
                    dataKey={source}
                    name={source.replace(/_/g, ' ')}
                    stroke={SOURCE_COLORS[idx % SOURCE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/40">No enrollment trend data available</p>
            <p className="text-xs text-white/30 mt-1">
              Trend data shows enrollment volume over the last 30 days
            </p>
          </div>
        )}
      </Card>

      {/* Campaign Performance */}
      <Card>
        <CardHeader
          title="Campaign Performance"
          subtitle="Performance metrics for each marketing campaign"
        />
        {campaigns.length > 0 ? (
          <>
            {/* Campaign Chart — enrollments + budget comparison */}
            <div className="px-4 pb-4" style={{ height: Math.max(180, campaigns.length * 40 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={campaigns} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis type="number" stroke="rgba(255,255,255,0.3)" fontSize={11} />
                  <YAxis type="category" dataKey="campaign_name" stroke="rgba(255,255,255,0.3)" fontSize={10} width={180} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="total_enrollments" name="Enrollments" fill="#14b8a6" radius={[0, 4, 4, 0]} barSize={14} />
                  <Bar dataKey="unique_students" name="Unique Students" fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={14} />
                  <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Campaign Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Campaign</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Source</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Enrollments</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Unique</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Budget</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Cost/Enroll</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">ROI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {campaigns.map((c: any) => (
                    <tr key={c.campaign_id || c.campaign_name} className="hover:bg-white/[0.02]">
                      <td className="py-3 px-4 text-white/90 font-medium">{c.campaign_name || 'Unnamed'}</td>
                      <td className="py-3 px-4"><span className="px-2 py-0.5 bg-white/[0.06] rounded text-xs text-white/60">{c.utm_source || 'N/A'}</span></td>
                      <td className="py-3 px-4 text-right text-teal-400 font-medium">{c.total_enrollments ?? 0}</td>
                      <td className="py-3 px-4 text-right text-white/50">{c.unique_students ?? '-'}</td>
                      <td className="py-3 px-4 text-right text-white/60">₹{(c.budget || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-white/90">{c.cost_per_enrollment ? `₹${c.cost_per_enrollment.toFixed(0)}` : '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.cost_per_enrollment && c.cost_per_enrollment < 200 ? 'bg-emerald-500/20 text-emerald-400'
                          : c.cost_per_enrollment && c.cost_per_enrollment < 500 ? 'bg-amber-500/20 text-amber-400'
                          : c.total_enrollments > 0 ? 'bg-red-500/20 text-red-400' : 'bg-white/[0.06] text-white/40'
                        }`}>{c.total_enrollments > 0 ? (c.cost_per_enrollment < 200 ? 'Great' : c.cost_per_enrollment < 500 ? 'Good' : 'High') : 'N/A'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-10">
            <Megaphone className="w-12 h-12 text-white/15 mx-auto mb-3" />
            <p className="text-white/50 font-medium">No campaigns created yet</p>
            <p className="text-xs text-white/30 mt-1 max-w-sm mx-auto">
              Create your first campaign to track enrollment ROI, cost per enrollment, and UTM attribution.
            </p>
          </div>
        )}
      </Card>

      {/* Magic Link Performance */}
      <Card>
        <CardHeader
          title="Magic Link Performance"
          subtitle="Click-through and conversion metrics for enrollment links"
        />
        {links.length > 0 ? (
          <>
            {/* Link Performance Chart */}
            {links.some((l) => l.total_clicks > 0) && (
              <div className="px-4 pb-4" style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={links.filter((l) => l.total_clicks > 0).slice(0, 8)} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="link_name" stroke="rgba(255,255,255,0.3)" fontSize={10} tick={{ fill: 'rgba(255,255,255,0.5)' }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Bar dataKey="total_clicks" name="Clicks" fill="#06b6d4" radius={[4, 4, 0, 0]} barSize={20} />
                    <Bar dataKey={(d: any) => d.successful_enrollments ?? d.total_enrollments ?? 0} name="Enrolled" fill="#14b8a6" radius={[4, 4, 0, 0]} barSize={20} />
                    <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Summary bar for links with zero clicks */}
            {links.every((l) => l.total_clicks === 0) && (
              <div className="mx-4 mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <MousePointerClick className="w-4 h-4 text-amber-400" />
                  <p className="text-sm text-amber-300">
                    Magic links exist but haven't been clicked yet. Share them to start tracking conversions.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-2 pb-2">
              {links.slice(0, 6).map((link) => (
                <div
                  key={link.link_id}
                  className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl hover:border-white/[0.12] transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-teal-400 shrink-0" />
                        <p className="text-white/90 font-medium truncate">{link.link_name || 'Unnamed Link'}</p>
                      </div>
                      <p className="text-sm text-white/50 mt-0.5 truncate">{link.exam_title}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ml-2 ${
                      link.is_active
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-white/[0.06] text-white/40'
                    }`}>
                      {link.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 rounded-lg bg-white/[0.02]">
                      <p className="text-lg font-bold text-cyan-400">{link.total_clicks}</p>
                      <p className="text-xs text-white/40">Clicks</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white/[0.02]">
                      <p className="text-lg font-bold text-teal-400">{(link as any).successful_enrollments ?? link.total_enrollments ?? 0}</p>
                      <p className="text-xs text-white/40">Enrolled</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white/[0.02]">
                      <p className="text-lg font-bold text-emerald-400">{link.conversion_rate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-white/40">Conv Rate</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {links.length > 6 && (
              <p className="text-xs text-white/40 text-center pb-3">
                Showing 6 of {links.length} links
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-10">
            <Link2 className="w-12 h-12 text-white/15 mx-auto mb-3" />
            <p className="text-white/50 font-medium">No magic links created yet</p>
            <p className="text-xs text-white/30 mt-1 max-w-sm mx-auto">
              Create enrollment links to track click-through and conversion performance for your exams.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};
